import { useEffect, useState } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { apiClient } from '../utils/apiClient';
import './OriginalEmailModal.css';

const IMAGES_KEY = 'original-email-load-images';

const readImagePref = () => {
  try { return localStorage.getItem(IMAGES_KEY) === '1'; } catch { return false; }
};

// The email is untrusted HTML. It is only ever shown inside an iframe that
// (1) has no allow-scripts, so nothing in it can run, (2) has no
// allow-same-origin, so it can't reach this app or its token, and (3) carries
// a CSP that blocks everything except inline styles and images. Remote images
// stay blocked until the user opts in, because loading them tells the sender
// the email was opened.
export const buildSrcDoc = (html, loadImages) => `<!doctype html>
<html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:${loadImages ? ' https:' : ''}; style-src 'unsafe-inline'; font-src data:">
<meta name="referrer" content="no-referrer">
<base target="_blank">
<style>body{margin:16px;background:#fff;color:#202124;font-family:Arial,Helvetica,sans-serif;font-size:14px}img{max-width:100%;height:auto}</style>
</head><body>${html}</body></html>`;

export default function OriginalEmailModal({ activityId, onClose }) {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState('');
  const [loadImages, setLoadImages] = useState(readImagePref);

  useEffect(() => {
    if (!activityId) return undefined;
    let cancelled = false;
    setHtml(null);
    setError('');
    apiClient.get(`/api/activities/${activityId}/original`)
      .then((data) => { if (!cancelled) setHtml(data.html); })
      .catch((err) => {
        if (!cancelled) setError(err.status === 404 ? 'No original is stored for this email.' : 'Could not load the original email.');
      });
    return () => { cancelled = true; };
  }, [activityId]);

  const toggleImages = () => {
    const next = !loadImages;
    setLoadImages(next);
    try { localStorage.setItem(IMAGES_KEY, next ? '1' : '0'); } catch { /* preference is best-effort */ }
  };

  return (
    <Modal
      open={!!activityId}
      onClose={onClose}
      title="Original email"
      size="large"
      footer={(
        <>
          <Button variant="secondary" onClick={toggleImages}>{loadImages ? 'Block remote images' : 'Load remote images'}</Button>
          <Button onClick={onClose}>Close</Button>
        </>
      )}
    >
      <p className="original-email-note">
        Shown in a safe view: scripts are disabled and remote images are {loadImages ? 'loaded' : 'blocked'}.
      </p>
      {error && <p className="original-email-error" role="alert">{error}</p>}
      {!error && html === null && <p aria-live="polite">Loading original email&hellip;</p>}
      {html !== null && (
        <iframe
          className="original-email-frame"
          title="Original email"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          srcDoc={buildSrcDoc(html, loadImages)}
        />
      )}
    </Modal>
  );
}
